// Ponte mínima entre tabSync e React Query / Supabase auth.
// Escopo deliberadamente pequeno: 3 efeitos, nada mais.
//
//   1. auth:signout  → força supabase.auth.signOut local (caso a outra aba
//      tenha deslogado) e invalida TODAS as queries (sessão morreu, cache
//      antigo pode estar com dados de outro user).
//   2. wallet:changed → invalida queryKey ['wallet'] e ['wallet-tx'].
//   3. orders:changed → invalida queryKey ['orders'] e ['my-orders'].
//
// Nada de state. Nada de fila. Sem refetch agressivo: invalidate marca
// stale e React Query só refaz se a query estiver ativa numa tela.
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tabSync } from "@/lib/tabSync";
import { supabase } from "@/integrations/supabase/client";
import { qk } from "@/lib/queryKeys";

export default function TabSyncBridge() {
  const qc = useQueryClient();

  useEffect(() => {
    tabSync.start();
    const off = tabSync.subscribe(async (event) => {
      switch (event.type) {
        case "auth:signout": {
          // Re-checa sessão local. Se outra aba deslogou, supabase já vai
          // ter limpado o storage compartilhado; chamamos signOut() apenas
          // para garantir que listeners onAuthStateChange disparem aqui.
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            await supabase.auth.signOut().catch(() => {});
            qc.clear();
          }
          break;
        }
        case "auth:signin": {
          // Cache anterior pode pertencer a outro user — limpa tudo.
          qc.clear();
          break;
        }
        case "wallet:changed": {
          qc.invalidateQueries({ queryKey: qk.wallet() });
          qc.invalidateQueries({ queryKey: qk.walletTx() });
          break;
        }
        case "orders:changed": {
          qc.invalidateQueries({ queryKey: qk.orders() });
          qc.invalidateQueries({ queryKey: qk.myOrders() });
          break;
        }
      }
    });
    return () => { off(); };
  }, [qc]);

  return null;
}
